import { styled } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useRubricCore } from '../../../../hooks/useRubricCore';
import type { ProjectRunAnalysisQuestion } from '../../../../Types/dealGPT';
import { FeedbackInterface } from '../../../../Types/feedback';
import { DownloadRubricCoreButton } from './DownloadRubricCoreButton';
import RubricSection from './RubricSection';
import { getUniqueAreas, groupByAreaAndDimension } from './rubricUtils';

const Section = styled('div')({
  '&:not(:first-of-type)': {
    borderTop: '1px solid #C4C4C4',
    paddingTop: '20px',
  },
});

type AreaMapType = {
  [area: string]: {
    [dimension: string]: ProjectRunAnalysisQuestion[];
  };
};

const sortDimensionsByDisplayOrder = (area: {
  [dimension: string]: ProjectRunAnalysisQuestion[];
}): ProjectRunAnalysisQuestion[][] => {
  const dimensionsArray = Object.values(area);

  dimensionsArray.sort((a, b) => {
    const displayOrderA = a[0]?.displayOrder ?? Number.MAX_SAFE_INTEGER;
    const displayOrderB = b[0]?.displayOrder ?? Number.MAX_SAFE_INTEGER;
    return displayOrderA - displayOrderB;
  });

  return dimensionsArray;
};

// Sample Area Map
// {
//   'Scaleability': {
//     'Compute & Storage': [{ sequence: 1, displayOrder: 1, area: 'Scaleability', dimension: 'Compute & Storage', createdAt: '2023-06-26T14:48:00.000Z', response: 'response1' }, { sequence: 1, displayOrder: 1, area: 'Scaleability', dimension: 'Compute & Storage', createdAt: '2023-06-24T14:48:00.000Z', response: 'response2' }],
//     'Network (WAN & LAN)': [{ sequence: 1, displayOrder: 2, area: 'Scaleability', dimension: 'Network (WAN & LAN)', createdAt: '2023-06-27T14:48:00.000Z', response: 'response2' }],
//     'Database & Caching': [{ sequence: 1, displayOrder: 3, area: 'Scaleability', dimension: 'Database & Caching', createdAt: '2023-06-28T14:48:00.000Z', response: 'response3' }]
//   },
//   'Availability': {
//     'Hosting (Infra & On-Prem/Cloud)': [{ sequence: 1, displayOrder: 4, area: 'Availability', dimension: 'Hosting (Infra & On-Prem/Cloud)', createdAt: '2023-06-28T14:48:00.000Z', response: 'response4' }]
//   }
// };

interface RubricPageProps {
  projectId: string;
  pageQuestions?: ProjectRunAnalysisQuestion[];
  refetchQuestions: () => void;
  setIsLoadingThis: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoadingApplyChanges: React.Dispatch<React.SetStateAction<boolean>>;
  expandAll: boolean;
  handleFeedbackSubmit: (feedback: FeedbackInterface, projectAnalysisQuestionId: string) => void;
  handleValidateFindingsClick: (questionId: string) => void;
}

const RubricPage: React.FC<RubricPageProps> = ({
  projectId,
  pageQuestions,
  refetchQuestions,
  setIsLoadingThis,
  setIsLoadingApplyChanges,
  expandAll,
  handleFeedbackSubmit,
  handleValidateFindingsClick,
}) => {
  const { scope } = useParams<{ scope: string }>();
  const [areas, setAreas] = useState<string[]>([]);
  const [areaMap, setAreaMap] = useState<AreaMapType>({});
  const { rubricCoreByArea, rubricType } = useRubricCore(scope);

  useEffect(() => {
    // Clear these out when switching pages
    setAreas([]);
    setAreaMap({});

    if (pageQuestions && pageQuestions.length > 0) {
      const areaMap = groupByAreaAndDimension(pageQuestions);
      setAreaMap(areaMap);
      const uniqueAreas = getUniqueAreas(pageQuestions);
      if (uniqueAreas) {
        setAreas(uniqueAreas);
      }
    }
  }, [pageQuestions]);

  return (
    <div>
      <div style={{ display: 'flex', gap: '16px', width: '100%', justifyContent: 'end' }}>
        <DownloadRubricCoreButton
          projectId={projectId}
          rubricType={rubricType}
          scope={scope}
          rubricCore={rubricCoreByArea}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {areas.length > 0 &&
          areas.map((area) => (
            <Section key={area}>
              <RubricSection
                projectId={projectId}
                areaName={area}
                dimensions={sortDimensionsByDisplayOrder(areaMap[area])}
                refetchQuestions={refetchQuestions}
                setIsLoadingThis={setIsLoadingThis}
                setIsLoadingApplyChanges={setIsLoadingApplyChanges}
                expandAll={expandAll}
                handleFeedbackSubmit={handleFeedbackSubmit}
                handleValidateFindingsClick={handleValidateFindingsClick}
                rubricType={rubricType}
                areaRubricCore={rubricCoreByArea[area]}
              />
            </Section>
          ))}
      </div>
    </div>
  );
};

export default RubricPage;
